import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslationPipe } from '@prekog/common-ui';
import { PressArticleFormMode } from './enum/press-article-form-mode.enum';
import { PressArticleForm } from './model/press-article-form.model';
import { PressArticle } from './model/press-article.model';

@Component({
    selector: 'pf-press-article',
    templateUrl: './press-article.component.html',
    styleUrls: ['./press-article.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, MatIconModule, TranslationPipe],
})
export class PressArticleComponent {
    private readonly _formBuilder = inject(FormBuilder);

    readonly pressArticleList = input<PressArticle[]>([]);

    readonly pressArticleListChange = output<PressArticle[]>();

    protected readonly QUOTATION_MAX_LENGTH = 1000;
    protected readonly LINK_MAX_LENGTH = 2048;
    protected readonly AUTHOR_MAX_LENGTH = 200;
    protected readonly LINK_NAME_MAX_LENGTH = 200;

    protected readonly PressArticleFormMode = PressArticleFormMode;

    protected readonly localArticles = signal<PressArticle[]>([]);
    protected readonly formMode = signal<PressArticleFormMode>(PressArticleFormMode.Add);
    protected readonly editingIndex = signal<number | null>(null);
    protected readonly isFormVisible = signal<boolean>(false);

    protected readonly articleForm: FormGroup<PressArticleForm> = this._buildForm();

    constructor() {
        effect(() => {
            this.localArticles.set([...(this.pressArticleList() ?? [])]);
        });
    }

    protected shouldShowError(controlName: keyof PressArticleForm): boolean {
        const control = this.articleForm.controls[controlName];
        return control.invalid && (control.dirty || control.touched);
    }

    protected openAddForm(): void {
        this.formMode.set(PressArticleFormMode.Add);
        this.editingIndex.set(null);
        this.articleForm.reset({ quotation: '', link: '', author: '', linkName: '' });
        this.isFormVisible.set(true);
    }

    protected openEditForm(index: number): void {
        const article = this.localArticles()[index];
        this.formMode.set(PressArticleFormMode.Edit);
        this.editingIndex.set(index);
        this.articleForm.setValue({
            quotation: article.quotation ?? '',
            link: article.link ?? '',
            author: article.author ?? '',
            linkName: article.linkName ?? '',
        });
        this.isFormVisible.set(true);
    }

    protected cancelForm(): void {
        this.isFormVisible.set(false);
        this.editingIndex.set(null);
        this.articleForm.reset({ quotation: '', link: '', author: '', linkName: '' });
    }

    protected saveArticle(): void {
        if (this.articleForm.invalid) {
            this.articleForm.markAllAsTouched();
            return;
        }

        const formValue = this.articleForm.getRawValue();
        const article: PressArticle = {
            quotation: formValue.quotation.trim(),
            link: formValue.link.trim(),
            author: formValue.author.trim() || undefined,
            linkName: formValue.linkName.trim() || undefined,
        };

        const index = this.editingIndex();
        if (this.formMode() === PressArticleFormMode.Edit && index !== null) {
            const existing = this.localArticles()[index];
            article.id = existing.id;
            const updated = [...this.localArticles()];
            updated[index] = article;
            this.localArticles.set(updated);
        } else {
            this.localArticles.set([...this.localArticles(), article]);
        }

        this._emitChange();
        this.cancelForm();
    }

    protected removeArticle(index: number): void {
        const updated = this.localArticles().filter((_article, articleIndex) => articleIndex !== index);
        this.localArticles.set(updated);
        this._emitChange();
        if (this.editingIndex() === index) {
            this.cancelForm();
        }
    }

    private _emitChange(): void {
        this.pressArticleListChange.emit([...this.localArticles()]);
    }

    private _buildForm(): FormGroup<PressArticleForm> {
        return this._formBuilder.nonNullable.group({
            quotation: ['', [Validators.required, Validators.maxLength(this.QUOTATION_MAX_LENGTH)]],
            link: ['', [Validators.required, Validators.maxLength(this.LINK_MAX_LENGTH)]],
            author: ['', [Validators.maxLength(this.AUTHOR_MAX_LENGTH)]],
            linkName: ['', [Validators.maxLength(this.LINK_NAME_MAX_LENGTH)]],
        });
    }
}
